import apiClient from './client';
import { API_PATHS } from './paths';
import type { ApiResponse, Page } from '../types/api';
import type {
  ClientBooking,
  ClientBookingDetails,
  ClientBookingItem,
  BookingListResponse,
  SmartHint,
  ConfirmationModel,
  CreateBookingReq,
  CreateBookingRes,
  CancelBookingReq,
  ClientCancelErrorKind,
  ClientBookingStatus,
  PaymentStatus,
} from '../types/client';

// ─── Raw types for new tab-based list endpoint ───────────────────────────────

interface RawBookingItem {
  id: number;
  bookingUuid: string;
  date: string;
  time: string;
  serviceId: number;
  serviceName: string;
  serviceProviderId: number; // → mapped to providerId
  providerName: string;
  durationMinutes: number;
  price: number;
  paymentLabel: string;
  status: ClientBookingStatus;
  canRequestCancel: boolean;
  cancellationDeadlinePassed: boolean;
  note: string | null;
  paymentStatus?: PaymentStatus | null;
}

interface RawBookingListResponse {
  items: RawBookingItem[];
  hint: SmartHint | null;
  nextCursor: string | null;
}

function mapBookingItem(raw: RawBookingItem): ClientBookingItem {
  return {
    id: raw.id,
    bookingUuid: raw.bookingUuid,
    date: raw.date,
    time: raw.time,
    serviceId: raw.serviceId,
    serviceName: raw.serviceName,
    providerId: raw.serviceProviderId,
    providerName: raw.providerName,
    durationMinutes: raw.durationMinutes,
    price: raw.price,
    paymentLabel: raw.paymentLabel,
    status: raw.status,
    canRequestCancel: raw.canRequestCancel,
    cancellationDeadlinePassed: raw.cancellationDeadlinePassed,
    note: raw.note,
    paymentStatus: raw.paymentStatus ?? null,
  };
}

// ─── Raw types for legacy list/detail endpoints ───────────────────────────────

// Backend response shape for booking list items
interface RawBooking {
  id: number;
  bookingUuid: string;
  serviceProviderId: number;
  serviceId: number;
  providerName?: string | null;
  serviceName?: string | null;
  durationMinutes?: number | null;
  startTime: string;
  endTime: string;
  status: ClientBookingStatus;
  confirmationModel: ConfirmationModel;
  totalAmount: number | null;
  customerNotes: string | null;
  cancellationReason: string | null;
  createdAt: string;
  cancellationDeadline?: string | null;
  paymentStatus?: PaymentStatus | null;
}

// Backend response shape for booking detail (new format: date/time/price)
interface RawBookingDetails {
  id: number;
  bookingUuid: string;
  date: string;
  time: string;
  serviceId: number;
  serviceName: string;
  providerId: number;
  providerName: string;
  durationMinutes: number;
  price: number;
  paymentLabel: string;
  status: ClientBookingStatus;
  canRequestCancel: boolean;
  cancellationDeadlinePassed: boolean;
  note: string | null;
  paymentStatus?: PaymentStatus | null;
}

function mapBooking(raw: RawBooking): ClientBooking {
  return {
    id: raw.id,
    bookingUuid: raw.bookingUuid,
    providerId: raw.serviceProviderId,
    serviceId: raw.serviceId,
    providerName: raw.providerName ?? null,
    serviceName: raw.serviceName ?? null,
    durationMinutes: raw.durationMinutes ?? null,
    startTime: raw.startTime,
    endTime: raw.endTime,
    status: raw.status,
    confirmationModel: raw.confirmationModel,
    totalAmount: raw.totalAmount,
    customerNotes: raw.customerNotes,
    cancellationReason: raw.cancellationReason,
    createdAt: raw.createdAt,
    cancellationDeadline: raw.cancellationDeadline ?? null,
    paymentStatus: raw.paymentStatus ?? null,
  };
}

export const clientBookingsApi = {
  getList: async (params?: { status?: ClientBookingStatus[] }): Promise<ClientBooking[]> => {
    const res = await apiClient.get<ApiResponse<Page<RawBooking>>>(API_PATHS.CLIENT_BOOKINGS, {
      params,
    });
    const content = res.data.data?.content;
    return Array.isArray(content) ? content.map(mapBooking) : [];
  },

  getByUuid: async (uuid: string): Promise<ClientBookingDetails> => {
    const res = await apiClient.get<ApiResponse<RawBookingDetails>>(
      API_PATHS.clientBookingByUuid(uuid),
    );
    const raw = res.data.data;
    return {
      id: raw.id,
      bookingUuid: raw.bookingUuid,
      date: raw.date,
      time: raw.time,
      serviceId: raw.serviceId,
      serviceName: raw.serviceName,
      providerId: raw.providerId,
      providerName: raw.providerName,
      durationMinutes: raw.durationMinutes,
      price: raw.price,
      paymentLabel: raw.paymentLabel,
      status: raw.status,
      canRequestCancel: raw.canRequestCancel,
      cancellationDeadlinePassed: raw.cancellationDeadlinePassed,
      note: raw.note,
      paymentStatus: raw.paymentStatus ?? null,
    };
  },

  create: async (req: CreateBookingReq): Promise<CreateBookingRes> => {
    const res = await apiClient.post<ApiResponse<CreateBookingRes>>(
      API_PATHS.CLIENT_BOOKINGS,
      req,
    );
    return res.data.data;
  },

  cancelBooking: async (uuid: string, req: CancelBookingReq): Promise<void> => {
    await apiClient.post(API_PATHS.clientBookingCancel(uuid), req);
  },

  getActive: async (): Promise<BookingListResponse> => {
    const res = await apiClient.get<ApiResponse<RawBookingListResponse>>(
      API_PATHS.clientBookingsTab('active', undefined, 50),
    );
    const raw = res.data.data;
    return {
      items: raw.items.map(mapBookingItem),
      hint: raw.hint,
      nextCursor: raw.nextCursor,
    };
  },

  getHistory: async (cursor?: string): Promise<BookingListResponse> => {
    const res = await apiClient.get<ApiResponse<RawBookingListResponse>>(
      API_PATHS.clientBookingsTab('history', cursor, 20),
    );
    const raw = res.data.data;
    return {
      items: raw.items.map(mapBookingItem),
      hint: raw.hint,
      nextCursor: raw.nextCursor,
    };
  },
};

// ─── Error classification helper ─────────────────────────────────────────────

export function classifyCancelError(err: unknown): { kind: ClientCancelErrorKind } {
  const axiosErr = err as {
    response?: { status?: number; data?: { code?: string } };
    code?: string;
  };
  const status = axiosErr?.response?.status;
  const code = axiosErr?.response?.data?.code;

  if (!navigator.onLine) return { kind: 'offline' };
  if (axiosErr?.code === 'ERR_NETWORK') return { kind: 'network' };
  if (status === 409 && code === 'BOOKING_CANCELLATION_DEADLINE_PASSED') return { kind: 'deadline' };
  return { kind: 'unknown' };
}
